import ReviewFileInput from '../../shared/ReviewFileInput';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaChevronLeft,
  FaChevronRight,
  FaCompress,
  FaDownload,
  FaExpand,
  FaFileAlt,
  FaFileExcel,
  FaFilePdf,
  FaFilePowerpoint,
  FaFileWord,
  FaImages,
  FaSearchMinus,
  FaSearchPlus,
  FaTable,
  FaTimes,
} from 'react-icons/fa';
import {
  PRESENTATION_ACCEPT,
  createLocalDeckId,
  validatePresentationFiles,
} from './presentationFiles';
import {
  extractRtfText,
  loadDocxPresentation,
  loadXlsxPreview,
  parseDelimitedPreview,
} from './officePreview';
import {
  clampPresentationZoom,
  getContainedPresentationSize,
} from './presentationLayout';
import {
  DOCX_RENDER_OPTIONS,
  DOCX_RENDERER_PACKAGE,
  hardenRenderedDocx,
  loadDocxRenderer,
} from './docxRenderer';
import { convertLegacyWordToPdf } from './legacyWordConversion';
import { loadPptxRenderer, PPTX_RENDERER_PACKAGE } from './pptxRenderer';

const naturalNameSort = (left, right) =>
  String(left?.name || '').localeCompare(String(right?.name || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });

const readLocalText = async (file) => {
  if (typeof file?.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The local text file could not be read.'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsText(file);
  });
};

const DeckIcon = ({ kind }) => {
  if (kind === 'pptx' || kind === 'guidance') return <FaFilePowerpoint aria-hidden="true" />;
  if (kind === 'pdf') return <FaFilePdf aria-hidden="true" />;
  if (kind === 'docx' || kind === 'doc' || kind === 'text' || kind === 'rtf') return <FaFileWord aria-hidden="true" />;
  if (kind === 'xlsx') return <FaFileExcel aria-hidden="true" />;
  if (kind === 'csv' || kind === 'tsv') return <FaTable aria-hidden="true" />;
  return <FaImages aria-hidden="true" />;
};

const FormattedWordParagraph = ({ paragraph }) => (
  <p
    className="mb-3 min-h-[1.25em] whitespace-pre-wrap break-words last:mb-0"
    style={paragraph?.style || undefined}
  >
    {(paragraph?.runs || []).length
      ? paragraph.runs.map((run, index) => (
        <span key={`word-run-${index}`} style={run.style || undefined}>{run.text}</span>
      ))
      : '\u00a0'}
  </p>
);

const FormattedWordDocument = ({ blocks }) => (
  <div className="mt-5 text-sm leading-normal text-slate-800" data-formatted-docx-preview="true">
    {(blocks || []).map((block, blockIndex) => {
      if (block.type === 'table') {
        return (
          <div className="my-4 overflow-x-auto" key={`word-table-${blockIndex}`}>
            <table className="w-full border-collapse">
              <tbody>
                {(block.rows || []).map((row, rowIndex) => (
                  <tr key={`word-row-${rowIndex}`}>
                    {(row.cells || []).map((cell, cellIndex) => (
                      <td className="border border-slate-300 p-2 align-top" key={`word-cell-${cellIndex}`}>
                        {(cell.paragraphs || []).map((paragraph, paragraphIndex) => (
                          <FormattedWordParagraph paragraph={paragraph} key={`word-cell-paragraph-${paragraphIndex}`} />
                        ))}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      return <FormattedWordParagraph paragraph={block} key={`word-paragraph-${blockIndex}`} />;
    })}
  </div>
);

export const PlainDocumentPreview = ({ preview, fileName, showChrome = true }) => (
  <article className="h-full w-full overflow-auto bg-white px-5 py-6 text-slate-900 sm:px-10 sm:py-9" data-safe-document-preview="true">
    {showChrome
      ? <h3 className="break-words border-b border-slate-200 pb-3 text-lg font-bold">{fileName}</h3>
      : <span className="sr-only">Structured fallback preview of {fileName}. {preview?.note}</span>}
    {showChrome && preview?.note && <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">{preview.note}</p>}
    {showChrome && preview?.truncated && <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-900">Preview limit reached. Open the original file to see the remaining content.</p>}
    {preview?.kind === 'docx' && preview?.blocks?.length
      ? <FormattedWordDocument blocks={preview.blocks} />
      : <pre className="mt-5 whitespace-pre-wrap break-words font-sans text-sm leading-7 text-slate-800">{preview?.text || 'This document contains no extractable text.'}</pre>}
  </article>
);

export const DocxDocumentPreview = ({ renderData, fallbackPreview, fileName }) => {
  const bodyRef = useRef(null);
  const styleRef = useRef(null);
  const [renderState, setRenderState] = useState('loading');

  useEffect(() => {
    const bodyContainer = bodyRef.current;
    const styleContainer = styleRef.current;
    if (!bodyContainer || !styleContainer || !renderData) {
      setRenderState('fallback');
      return undefined;
    }

    let cancelled = false;
    bodyContainer.replaceChildren();
    styleContainer.replaceChildren();
    setRenderState('loading');

    const render = async () => {
      const detachedBody = document.createElement('div');
      const detachedStyles = document.createElement('div');
      try {
        const module = await loadDocxRenderer();
        if (cancelled) return;
        if (typeof module?.renderAsync !== 'function') {
          throw new Error('The DOCX renderer did not expose its render API.');
        }
        await module.renderAsync(
          renderData,
          detachedBody,
          detachedStyles,
          DOCX_RENDER_OPTIONS
        );
        if (cancelled) return;
        // Sanitize while detached so untrusted links, frames, remote CSS URLs,
        // and active elements never enter the live meeting document.
        hardenRenderedDocx(detachedBody, detachedStyles);
        if (cancelled) return;
        bodyContainer.replaceChildren(...Array.from(detachedBody.childNodes));
        styleContainer.replaceChildren(...Array.from(detachedStyles.childNodes));
        setRenderState('ready');
      } catch (renderError) {
        if (cancelled) return;
        bodyContainer.replaceChildren();
        styleContainer.replaceChildren();
        setRenderState('fallback');
        console.warn('High-fidelity local DOCX rendering failed:', renderError);
      }
    };
    render();

    return () => {
      cancelled = true;
      bodyContainer.replaceChildren();
      styleContainer.replaceChildren();
    };
  }, [renderData]);

  return (
    <article
      className="relative h-full w-full overflow-hidden bg-slate-300 text-slate-950"
      aria-label={`Full-page preview of ${fileName}`}
      aria-busy={renderState === 'loading'}
      data-high-fidelity-docx-preview="true"
    >
      <div ref={styleRef} className="hidden" aria-hidden="true" data-docx-style-host="true" />
      <div
        ref={bodyRef}
        className={`h-full w-full overflow-auto bg-slate-300 ${renderState === 'fallback' ? 'hidden' : ''}`}
        style={{ colorScheme: 'light' }}
        data-docx-page-host="true"
      />
      {renderState === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90 px-5 text-center text-sm font-semibold text-white">
          Rendering the original Word pages locally…
        </div>
      )}
      {renderState === 'fallback' && (
        <div className="absolute inset-0">
          <PlainDocumentPreview
            preview={fallbackPreview}
            fileName={fileName}
            showChrome={false}
          />
        </div>
      )}
      <span className="sr-only">Embedded images, numbering, fonts, tables, headers and footers are rendered locally. Active content and external navigation are disabled.</span>
    </article>
  );
};

const TablePreview = ({ sheet, note }) => {
  const rows = sheet?.rows || [];
  const columnCount = rows.reduce((maximum, row) => Math.max(maximum, row.length), 0);
  return (
    <article className="flex h-full w-full min-h-0 flex-col overflow-hidden bg-white text-slate-900" data-safe-table-preview="true">
      <div className="shrink-0 border-b border-slate-200 px-4 py-3">
        <h3 className="font-bold">{sheet?.name || 'Table preview'}</h3>
        {note && <p className="mt-1 text-xs leading-5 text-amber-800">{note}</p>}
        {sheet?.truncated && <p className="mt-1 text-xs font-semibold text-rose-700">Preview limits reached; open the original file for all rows and columns.</p>}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {rows.length ? (
          <table className="min-w-full border-collapse text-left text-xs">
            <thead className="sticky top-0 z-10 bg-slate-100">
              <tr>
                <th className="border border-slate-300 px-2 py-1.5 text-center text-slate-500">#</th>
                {Array.from({ length: columnCount }, (_, index) => (
                  <th key={`column-${index}`} className="border border-slate-300 px-3 py-1.5 font-semibold">{index + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`} className="odd:bg-white even:bg-slate-50">
                  <th className="sticky left-0 border border-slate-200 bg-slate-100 px-2 py-1.5 text-center text-slate-500">{rowIndex + 1}</th>
                  {Array.from({ length: columnCount }, (_, columnIndex) => (
                    <td key={`cell-${rowIndex}-${columnIndex}`} className="max-w-[28rem] whitespace-pre-wrap break-words border border-slate-200 px-3 py-1.5 align-top">{row[columnIndex] || ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="p-8 text-center text-sm text-slate-500">No stored table values were found.</p>
        )}
      </div>
    </article>
  );
};

const GuidancePreview = ({ deck }) => (
  <article className="flex h-full w-full flex-col items-center justify-center overflow-auto bg-slate-900 p-6 text-center text-white" data-office-conversion-guidance="true">
    <FaFileAlt className="text-5xl text-amber-300" aria-hidden="true" />
    <h3 className="mt-4 max-w-xl break-words text-xl font-bold">{deck.fileName}</h3>
    <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-200">{deck.guidance}</p>
    <p className="mt-3 max-w-2xl text-xs leading-5 text-amber-200">No document content has been fabricated. The file remains on this device and is not sent through presentation synchronization.</p>
    <a href={deck.objectUrls[0]} download={deck.fileName} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-indigo-500 px-4 py-2.5 text-sm font-bold hover:bg-indigo-400">
      <FaDownload aria-hidden="true" /> Open or download original
    </a>
  </article>
);

const StageToolbar = ({ disabled, zoomPercent, onFit, onZoom, fullscreen, onFullscreen }) => (
  <div className="flex shrink-0 flex-wrap items-center justify-center gap-1.5 border-b border-white/10 bg-slate-900/95 px-2 py-2 text-xs text-white" aria-label="Presentation fit and zoom controls">
    <button type="button" disabled={disabled} onClick={onFit} className="rounded-lg bg-slate-700 px-3 py-1.5 font-bold hover:bg-slate-600 disabled:opacity-40" aria-label="Fit full presentation to stage">Fit</button>
    <button type="button" disabled={disabled || zoomPercent <= 50} onClick={() => onZoom(zoomPercent - 10)} className="rounded-lg bg-slate-700 p-2 hover:bg-slate-600 disabled:opacity-40" aria-label="Zoom presentation out"><FaSearchMinus /></button>
    <span className="min-w-[3.5rem] text-center font-mono font-bold tabular-nums" aria-live="polite">{zoomPercent}%</span>
    <button type="button" disabled={disabled || zoomPercent >= 200} onClick={() => onZoom(zoomPercent + 10)} className="rounded-lg bg-slate-700 p-2 hover:bg-slate-600 disabled:opacity-40" aria-label="Zoom presentation in"><FaSearchPlus /></button>
    <button type="button" onClick={onFullscreen} className="ml-1 inline-flex items-center gap-1.5 rounded-lg border border-indigo-300/35 bg-indigo-500/15 px-3 py-1.5 font-bold text-indigo-100 hover:bg-indigo-500/25" aria-label={fullscreen ? 'Exit presentation fullscreen' : 'Enter presentation fullscreen'}>
      {fullscreen ? <FaCompress /> : <FaExpand />}{fullscreen ? 'Exit full screen' : 'Full screen'}
    </button>
  </div>
);

const LocalPresentationStudio = ({
  open,
  onClose,
  roomId = '',
  screenSharing = false,
  publishPresentationState,
  presentationClearRevision = 0,
  presentationSyncError = '',
}) => {
  const fileInputRef = useRef(null);
  const pptxContainerRef = useRef(null);
  const stageViewportRef = useRef(null);
  const stageShellRef = useRef(null);
  const viewerRef = useRef(null);
  const deckRef = useRef(null);
  const activeSyncRef = useRef(null);
  const selectionRevisionRef = useRef(0);
  const previousOpenRef = useRef(open);
  const clearRevisionRef = useRef(presentationClearRevision);
  const [deck, setDeck] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [slideCount, setSlideCount] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [contentSize, setContentSize] = useState({ width: 16, height: 9 });
  const [stageBounds, setStageBounds] = useState({ width: 960, height: 540 });
  const [fullscreen, setFullscreen] = useState(false);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);

  const destroyViewer = useCallback(() => {
    try { viewerRef.current?.destroy?.(); } catch {}
    viewerRef.current = null;
    if (pptxContainerRef.current) pptxContainerRef.current.replaceChildren();
  }, []);

  const releaseDeck = useCallback((target = deckRef.current) => {
    (target?.objectUrls || []).forEach((url) => {
      try { URL.revokeObjectURL(url); } catch {}
    });
    if (deckRef.current === target) deckRef.current = null;
  }, []);

  const stopActivePresentation = useCallback(() => {
    const active = activeSyncRef.current;
    if (!active) return false;
    activeSyncRef.current = null;
    setSyncEnabled(false);
    publishPresentationState?.({ ...active, status: 'stopped' });
    return true;
  }, [publishPresentationState]);

  const resetDeck = useCallback(() => {
    selectionRevisionRef.current += 1;
    destroyViewer();
    releaseDeck();
    activeSyncRef.current = null;
    setDeck(null);
    setSlideIndex(0);
    setSlideCount(0);
    setError('');
    setLoading(false);
    setSyncEnabled(false);
    setZoomPercent(100);
    setContentSize({ width: 16, height: 9 });
    setActiveSheetIndex(0);
  }, [destroyViewer, releaseDeck]);

  useEffect(() => {
    if (previousOpenRef.current && !open) {
      stopActivePresentation();
      resetDeck();
    }
    previousOpenRef.current = open;
  }, [open, resetDeck, stopActivePresentation]);

  useEffect(() => {
    if (clearRevisionRef.current === presentationClearRevision) return;
    clearRevisionRef.current = presentationClearRevision;
    activeSyncRef.current = null;
    resetDeck();
  }, [presentationClearRevision, resetDeck]);

  const closeStudio = useCallback(() => {
    stopActivePresentation();
    resetDeck();
    onClose?.();
  }, [onClose, resetDeck, stopActivePresentation]);

  useEffect(() => () => {
    selectionRevisionRef.current += 1;
    const active = activeSyncRef.current;
    if (active) publishPresentationState?.({ ...active, status: 'stopped' });
    activeSyncRef.current = null;
    try { viewerRef.current?.destroy?.(); } catch {}
    (deckRef.current?.objectUrls || []).forEach((url) => {
      try { URL.revokeObjectURL(url); } catch {}
    });
  }, [publishPresentationState]);

  const selectFiles = useCallback(async (event) => {
    const validation = validatePresentationFiles(event.target.files);
    event.target.value = '';
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    stopActivePresentation();
    destroyViewer();
    releaseDeck();
    const selectionRevision = selectionRevisionRef.current + 1;
    selectionRevisionRef.current = selectionRevision;
    setLoading(['pptx', 'docx', 'doc', 'xlsx'].includes(validation.kind));
    setError('');
    setSlideIndex(0);
    setSyncEnabled(false);
    setZoomPercent(100);
    setContentSize({ width: 16, height: 9 });
    setActiveSheetIndex(0);

    let objectUrls = [];
    try {
      const files = validation.kind === 'image'
        ? [...validation.files].sort(naturalNameSort)
        : validation.files;
      objectUrls = ['pptx', 'doc', 'docx'].includes(validation.kind)
        ? []
        : files.map((file) => URL.createObjectURL(file));
      let preview = null;
      let docxRenderData = null;
      if (validation.kind === 'docx') {
        const docxPresentation = await loadDocxPresentation(files[0]);
        preview = docxPresentation.preview;
        docxRenderData = docxPresentation.renderData;
      }
      if (validation.kind === 'doc') {
        try {
          const convertedPdf = await convertLegacyWordToPdf({ roomId, file: files[0] });
          objectUrls = [URL.createObjectURL(convertedPdf)];
        } catch (conversionError) {
          const detail = conversionError?.response?.data?.message || conversionError?.message;
          throw new Error(`Legacy Word conversion failed${detail ? `: ${detail}` : '. Please try again or convert the file to DOCX/PDF.'}`);
        }
      }
      if (validation.kind === 'xlsx') preview = await loadXlsxPreview(files[0]);
      if (['text', 'csv', 'tsv', 'rtf'].includes(validation.kind)) {
        const source = await readLocalText(files[0]);
        if (validation.kind === 'csv' || validation.kind === 'tsv') {
          preview = {
            kind: validation.kind,
            sheets: [{
              name: files[0].name,
              ...parseDelimitedPreview(source, validation.kind === 'tsv' ? '\t' : ','),
            }],
            note: `${validation.kind.toUpperCase()} values are parsed locally; no formulas or external content are executed.`,
          };
        } else if (validation.kind === 'rtf') {
          preview = {
            kind: 'rtf',
            ...extractRtfText(source),
            note: 'Safe RTF plain-text extraction. Formatting, embedded objects and external links are intentionally omitted.',
          };
        } else {
          preview = {
            kind: 'text',
            text: source.slice(0, 1_000_000),
            truncated: source.length > 1_000_000,
            note: files[0].name.toLowerCase().match(/\.(md|markdown)$/)
              ? 'Markdown is shown as safe plain text; HTML and scripts are not rendered.'
              : 'Plain-text preview rendered locally.',
          };
        }
      }
      const nextDeck = {
        id: createLocalDeckId(),
        kind: validation.kind,
        relayKind: validation.relayKind,
        guidance: validation.guidance,
        fileName: validation.kind === 'image'
          ? `${files.length} slide images`
          : files[0].name,
        files,
        objectUrls,
        preview,
        docxRenderData,
        pptxBuffer: validation.kind === 'pptx'
          ? await files[0].arrayBuffer()
          : null,
      };
      if (selectionRevisionRef.current !== selectionRevision) {
        objectUrls.forEach((url) => {
          try { URL.revokeObjectURL(url); } catch {}
        });
        return;
      }
      deckRef.current = nextDeck;
      setDeck(nextDeck);
      setSlideCount(validation.kind === 'image' ? files.length : validation.kind === 'pptx' ? 0 : 1);
      setLoading(false);
    } catch (selectionError) {
      objectUrls.forEach((url) => {
        try { URL.revokeObjectURL(url); } catch {}
      });
      if (selectionRevisionRef.current !== selectionRevision) return;
      setLoading(false);
      setError(selectionError?.message || 'The local presentation could not be read safely.');
    }
  }, [destroyViewer, releaseDeck, roomId, stopActivePresentation]);

  useEffect(() => {
    if (!open || deck?.kind !== 'pptx' || !deck.pptxBuffer || !pptxContainerRef.current) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    const render = async () => {
      try {
        const module = await loadPptxRenderer();
        if (cancelled) return;
        if (typeof module?.PptxViewer?.open !== 'function') {
          throw new Error('The PPTX renderer did not expose its viewer API.');
        }
        const viewer = await module.PptxViewer.open(
          deck.pptxBuffer,
          pptxContainerRef.current,
          {
            fitMode: 'contain',
            zoomPercent: 100,
            renderMode: 'slide',
            lazyMedia: true,
            lazySlides: true,
            pdfjs: false,
            ...(module.RECOMMENDED_ZIP_LIMITS
              ? { zipLimits: module.RECOMMENDED_ZIP_LIMITS }
              : {}),
          }
        );
        if (cancelled) {
          viewer?.destroy?.();
          return;
        }
        viewerRef.current = viewer;
        const count = Math.max(1, Number(viewer.slideCount) || 1);
        const slideWidth = Math.max(1, Number(viewer.slideWidth) || 16);
        const slideHeight = Math.max(1, Number(viewer.slideHeight) || 9);
        setContentSize({ width: slideWidth, height: slideHeight });
        setSlideCount(count);
        setSlideIndex(Math.min(Number(viewer.currentSlideIndex) || 0, count - 1));
        setLoading(false);
      } catch (renderError) {
        if (cancelled) return;
        setLoading(false);
        setError('PPTX rendering is unavailable in this browser. Export the deck as PDF or slide images and open that local fallback instead.');
        console.warn('Local PPTX renderer failed:', renderError);
      }
    };
    render();
    return () => {
      cancelled = true;
      try { viewerRef.current?.destroy?.(); } catch {}
      viewerRef.current = null;
    };
  }, [deck, open]);

  useEffect(() => {
    if (deck?.kind !== 'pptx' || !viewerRef.current || loading) return;
    Promise.resolve(viewerRef.current.goToSlide?.(slideIndex)).catch((navigationError) => {
      setError(navigationError?.message || 'Unable to render that slide.');
    });
  }, [deck?.kind, loading, slideIndex]);

  useEffect(() => {
    if (!syncEnabled || !deck) return;
    const values = {
      deckId: deck.id,
      fileName: deck.fileName,
      kind: deck.relayKind || undefined,
      slideIndex,
      slideCount,
      aspectRatio: `${contentSize.width}:${contentSize.height}`,
    };
    activeSyncRef.current = values;
    publishPresentationState?.({ ...values, status: 'slide' });
  }, [contentSize.height, contentSize.width, deck, publishPresentationState, slideCount, slideIndex, syncEnabled]);

  useEffect(() => {
    if (screenSharing || !activeSyncRef.current) return;
    stopActivePresentation();
  }, [screenSharing, stopActivePresentation]);

  const move = useCallback((offset) => {
    if (!deck || !['pptx', 'image'].includes(deck.kind)) return;
    setSlideIndex((current) => Math.max(0, Math.min(slideCount - 1, current + offset)));
  }, [deck, slideCount]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      if (event.key === 'ArrowRight' || event.key === 'PageDown') {
        event.preventDefault();
        move(1);
      }
      if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault();
        move(-1);
      }
      if (event.key === 'Escape' && !document.fullscreenElement) closeStudio();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeStudio, move, open]);

  useEffect(() => {
    const viewport = stageViewportRef.current;
    if (!open || !viewport) return undefined;
    const measure = () => {
      const rect = viewport.getBoundingClientRect();
      setStageBounds({
        width: Math.max(1, rect.width || viewport.clientWidth || window.innerWidth),
        height: Math.max(1, rect.height || viewport.clientHeight || window.innerHeight),
      });
    };
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(viewport);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  useEffect(() => {
    const update = () => setFullscreen(document.fullscreenElement === stageShellRef.current);
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement === stageShellRef.current) await document.exitFullscreen?.();
      else if (stageShellRef.current?.requestFullscreen) await stageShellRef.current.requestFullscreen();
      else setError('Presentation full screen is not supported by this browser.');
    } catch {
      setError('The browser could not enter presentation full screen.');
    }
  }, []);

  const containedKinds = deck && ['pptx', 'image'].includes(deck.kind);
  const surfaceSize = useMemo(() => {
    if (containedKinds) {
      return getContainedPresentationSize({
        viewportWidth: stageBounds.width,
        viewportHeight: stageBounds.height,
        contentWidth: contentSize.width,
        contentHeight: contentSize.height,
        zoomPercent,
        padding: 24,
      });
    }
    const scale = clampPresentationZoom(zoomPercent) / 100;
    return {
      width: Math.max(280, Math.floor(Math.max(320, stageBounds.width - 24) * scale)),
      height: Math.max(220, Math.floor(Math.max(260, stageBounds.height - 24) * scale)),
    };
  }, [containedKinds, contentSize.height, contentSize.width, stageBounds.height, stageBounds.width, zoomPercent]);

  if (!open) return null;

  const hasNavigation = deck && ['pptx', 'image'].includes(deck.kind) && slideCount > 0;
  const showPptxFallback = deck?.kind === 'pptx' && error;
  const tableSheets = deck?.preview?.sheets || [];
  const activeSheet = tableSheets[Math.min(activeSheetIndex, Math.max(0, tableSheets.length - 1))];
  const scrollCanvasStyle = {
    width: Math.max(stageBounds.width, surfaceSize.width + 24),
    height: Math.max(stageBounds.height, surfaceSize.height + 24),
  };

  return (
    <section
      className="absolute inset-x-0 top-[3.5rem] z-[220] flex min-h-0 flex-col bg-slate-950 text-white shadow-2xl"
      style={{ bottom: 'var(--meeting-controls-height, 5.75rem)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="local-presentation-title"
      data-local-presentation-studio="true"
    >
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-slate-900 px-3 py-2.5 sm:px-4 sm:py-3">
        <div className="min-w-0">
          <h2 id="local-presentation-title" className="text-base font-bold sm:text-lg">Local presentation · Presenter view</h2>
          <p className="max-w-[72vw] truncate text-xs text-slate-400">
            {deck?.fileName || 'Present slides, PDFs, documents, text, tables, images, or conversion guidance locally.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ReviewFileInput ref={fileInputRef} type="file" accept={PRESENTATION_ACCEPT} multiple onChange={selectFiles} className="sr-only" aria-label="Choose local presentation or document" />
          <button type="button" onClick={() => fileInputRef.current?.click()} className="rounded-xl border border-indigo-300/40 bg-indigo-500/15 px-3 py-2 text-xs font-bold text-indigo-100 hover:bg-indigo-500/25">{deck ? 'Change file' : 'Choose file'}</button>
          <button type="button" onClick={closeStudio} className="rounded-full p-2 text-slate-300 hover:bg-white/10 hover:text-white" aria-label="Close presenter view"><FaTimes /></button>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <main ref={stageShellRef} className="relative flex min-h-0 flex-col overflow-hidden bg-black" data-presentation-stage-shell="true">
          <StageToolbar disabled={!deck} zoomPercent={zoomPercent} onFit={() => setZoomPercent(100)} onZoom={(value) => setZoomPercent(clampPresentationZoom(value))} fullscreen={fullscreen} onFullscreen={toggleFullscreen} />
          <div ref={stageViewportRef} className="min-h-0 flex-1 overflow-auto bg-black" data-presentation-stage-viewport="true">
            <div className="flex items-center justify-center" style={scrollCanvasStyle}>
              <div
                className={`relative shrink-0 overflow-hidden rounded-2xl border border-white/10 shadow-2xl ${['pptx', 'pdf', 'doc', 'image'].includes(deck?.kind) ? 'bg-white' : 'bg-slate-900'}`}
                style={{ width: surfaceSize.width, height: surfaceSize.height }}
                data-presentation-fit-surface="true"
                data-presentation-zoom={zoomPercent}
              >
                {!deck && !loading && (
                  <div className="flex h-full w-full flex-col items-center justify-center overflow-auto p-6 text-center">
                    <FaFilePowerpoint className="text-5xl text-orange-400" aria-hidden="true" />
                    <h3 className="mt-4 text-xl font-bold">Open a local presentation or document</h3>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">PPTX, PDF, images, DOCX, XLSX, TXT, Markdown, RTF, CSV and TSV render locally. Legacy DOC securely converts to a temporary PDF; other legacy office formats show truthful conversion guidance.</p>
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="mt-5 rounded-xl bg-indigo-500 px-5 py-2.5 text-sm font-bold hover:bg-indigo-400">Choose local file</button>
                  </div>
                )}

                {deck?.kind === 'pptx' && <div ref={pptxContainerRef} className="h-full w-full overflow-hidden" data-pptx-slide-host="true" />}

                {['pdf', 'doc'].includes(deck?.kind) && (
                  <object className="h-full w-full bg-white" data={`${deck.objectUrls[0]}#page=1&view=Fit&zoom=page-fit&toolbar=1&navpanes=0`} type="application/pdf" aria-label={deck.fileName} data-pdf-page-fit="true">
                    <div className="flex h-full w-full flex-col items-center justify-center bg-slate-900 p-8 text-center">
                      <p>This browser cannot preview the PDF inline.</p>
                      <a className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-500 px-4 py-2 font-bold" href={deck.objectUrls[0]} download={deck.kind === 'doc' ? deck.fileName.replace(/\.doc$/i, '.pdf') : deck.fileName}><FaDownload /> Open local PDF</a>
                    </div>
                  </object>
                )}

                {deck?.kind === 'image' && (
                  <img className="h-full w-full object-contain" src={deck.objectUrls[slideIndex]} alt={`Presentation slide ${slideIndex + 1}`} onLoad={(event) => setContentSize({ width: event.currentTarget.naturalWidth || 16, height: event.currentTarget.naturalHeight || 9 })} />
                )}

                {deck?.kind === 'docx' && <DocxDocumentPreview renderData={deck.docxRenderData} fallbackPreview={deck.preview} fileName={deck.fileName} />}
                {['text', 'rtf'].includes(deck?.kind) && <PlainDocumentPreview preview={deck.preview} fileName={deck.fileName} />}
                {['xlsx', 'csv', 'tsv'].includes(deck?.kind) && <TablePreview sheet={activeSheet} note={deck.preview?.note} />}
                {deck?.kind === 'guidance' && <GuidancePreview deck={deck} />}

                {loading && <div className="absolute inset-0 flex items-center justify-center bg-slate-950/95 px-5 text-center text-sm font-semibold">Reading and rendering the file locally…</div>}
                {showPptxFallback && <div className="absolute inset-0 flex items-center justify-center bg-slate-950 p-6 text-center text-sm leading-6 text-amber-100">{error}</div>}
                {error && !showPptxFallback && <div className="absolute inset-x-3 bottom-3 z-20 rounded-xl border border-rose-300/30 bg-rose-950/95 px-4 py-3 text-center text-xs leading-5 text-rose-100" role="alert">{error}</div>}
              </div>
            </div>
          </div>
        </main>

        <aside className="min-h-0 overflow-y-auto border-l border-white/10 bg-slate-900 p-4">
          {deck && <>
            <div className="flex items-center gap-3 rounded-2xl bg-slate-800 p-3">
              <span className="text-2xl text-orange-300"><DeckIcon kind={deck.kind} /></span>
              <div className="min-w-0">
                <p className="truncate text-sm font-bold">{deck.fileName}</p>
                <p className="text-xs uppercase tracking-wide text-slate-400">{deck.kind === 'image' ? 'Image deck' : deck.relayKind || deck.kind}</p>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-slate-950 p-4 text-center">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Current view</p>
              <p className="mt-1 font-mono text-2xl font-black">{hasNavigation ? `${slideIndex + 1} / ${slideCount || '…'}` : ['pdf', 'doc'].includes(deck.kind) ? 'PDF' : activeSheet?.name || 'Document'}</p>
              {['pdf', 'doc'].includes(deck.kind) && <p className="mt-2 text-xs leading-5 text-slate-400">The embedded PDF viewer uses full-page fit. Its own toolbar controls pages.</p>}
            </div>

            {tableSheets.length > 1 && (
              <label className="mt-4 block text-xs font-bold text-slate-300">Worksheet
                <select value={activeSheetIndex} onChange={(event) => setActiveSheetIndex(Number(event.target.value) || 0)} className="mt-1 w-full rounded-lg border border-white/15 bg-slate-800 px-3 py-2 text-sm text-white">
                  {tableSheets.map((sheet, index) => <option value={index} key={`${sheet.name}-${index}`}>{sheet.name}</option>)}
                </select>
              </label>
            )}

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button type="button" disabled={!hasNavigation || slideIndex <= 0} onClick={() => move(-1)} className="rounded-xl bg-slate-700 px-3 py-3 text-sm font-bold hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-35"><FaChevronLeft className="mr-2 inline" /> Previous</button>
              <button type="button" disabled={!hasNavigation || slideIndex >= slideCount - 1} onClick={() => move(1)} className="rounded-xl bg-slate-700 px-3 py-3 text-sm font-bold hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-35">Next <FaChevronRight className="ml-2 inline" /></button>
            </div>

            <div className={`mt-5 rounded-2xl border p-4 ${screenSharing ? 'border-emerald-300/35 bg-emerald-500/10' : 'border-amber-300/35 bg-amber-500/10'}`}>
              <p className={`text-sm font-bold ${screenSharing ? 'text-emerald-200' : 'text-amber-200'}`}>{screenSharing ? 'Screen share is active' : 'Start screen sharing to show this view'}</p>
              <p className="mt-2 text-xs leading-5 text-slate-300">
                {deck.kind === 'doc'
                  ? 'This legacy DOC was sent only to the authenticated meeting conversion endpoint. The temporary converted PDF stays in this presenter; only bounded presenter metadata is synchronized.'
                  : 'Use Share and choose this tab, window, or screen. Only bounded metadata is synchronized; local file bytes and object URLs never leave this device.'}
              </p>
              <button
                type="button"
                disabled={!screenSharing}
                onClick={() => {
                  if (activeSyncRef.current) {
                    stopActivePresentation();
                    return;
                  }
                  const values = {
                    deckId: deck.id,
                    fileName: deck.fileName,
                    kind: deck.relayKind || undefined,
                    slideIndex,
                    slideCount,
                    aspectRatio: `${contentSize.width}:${contentSize.height}`,
                  };
                  const queued = publishPresentationState?.({ ...values, status: 'started' });
                  if (queued !== false) {
                    activeSyncRef.current = values;
                    setSyncEnabled(true);
                  }
                }}
                className={`mt-3 w-full rounded-xl px-4 py-2.5 text-sm font-bold ${syncEnabled ? 'bg-rose-500 hover:bg-rose-400' : 'bg-emerald-500 text-slate-950 hover:bg-emerald-400'} disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400`}
              >
                {syncEnabled ? 'Stop presenter state' : 'Sync presenter state'}
              </button>
              {presentationSyncError && <p className="mt-3 rounded-lg border border-rose-300/25 bg-rose-950/65 px-3 py-2 text-xs leading-5 text-rose-100" role="alert">{presentationSyncError}</p>}
            </div>
          </>}

          <p className="mt-4 break-all text-[10px] leading-4 text-slate-500" title={`${PPTX_RENDERER_PACKAGE} · ${DOCX_RENDERER_PACKAGE}`}>PPTX engine: @aiden0z/pptx-renderer 1.2.4 · DOCX engine: docx-preview 0.4.0 · bounded JSZip 3.10.1{deck?.kind === 'doc' ? ' · DOC converted by authenticated meeting API' : ' · all local'}</p>
        </aside>
      </div>
    </section>
  );
};

export default LocalPresentationStudio;
